import Phaser from 'phaser'
import SceneKeys from '../consts/SceneKeys'

export default class StartScreen extends Phaser.Scene {
  private isMobile = false

  constructor() {
    super(SceneKeys.StartScreen)
  }

  create() {
    const { width, height } = this.scale
    const centerX = width * 0.5
    const centerY = height * 0.5

    // Detect mobile
    this.isMobile = this.sys.game.device.input.touch || !this.sys.game.device.os.desktop

    // Dark background
    const overlay = this.add.rectangle(0, 0, width, height, 0x0a0a1a, 1)
    overlay.setOrigin(0, 0)

    // Title
    const title = this.add
      .text(centerX, 80, 'BRITRUNNER', {
        fontSize: '64px',
        color: '#FFD700',
        fontStyle: 'bold',
        stroke: '#8B4513',
        strokeThickness: 6,
        shadow: { offsetX: 4, offsetY: 4, color: '#000000', blur: 8, fill: true },
      })
      .setOrigin(0.5)

    // Animate title
    this.tweens.add({
      targets: title,
      scale: 1.1,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })

    // Story/Instructions box
    const boxY = centerY
    const boxWidth = Math.min(width - 100, 900)
    const boxHeight = 420
    const boxX = centerX - boxWidth / 2
    const boxTop = boxY - boxHeight / 2

    // Box background
    const box = this.add.graphics()
    box.fillStyle(0x1a1a2e, 0.9)
    box.fillRoundedRect(boxX, boxTop, boxWidth, boxHeight, 15)
    box.lineStyle(4, 0xffd700, 1)
    box.strokeRoundedRect(boxX, boxTop, boxWidth, boxHeight, 15)

    // Create a mask for the story text area (only show text inside the story section)
    const storyAreaHeight = 240 // Area for story text
    const storyMask = this.make.graphics({ x: 0, y: 0, add: false })
    storyMask.fillStyle(0xffffff)
    storyMask.fillRect(boxX + 20, boxTop + 10, boxWidth - 40, storyAreaHeight)

    const geometryMask = storyMask.createGeometryMask()

    // Story text with word wrapping
    const storyText = `BritRunner: Reclaim the Realm
The Union Jack Rises!

Once the scepter'd isle of kings and queens, Great Britain
now cowers under a digital panopticon—its streets swarmed
by invasive drones, its pavements paved with alien banners:
the green crescent of invaders, the rainbow siege of the
trans tide, the crescent stars of Pakistan's shadow, and more.

The government watches. The skies buzz with enforcers.
But you? You're the rogue patriot, armed with the mightiest
flag of all—the Union Jack.

Throw it down! Blanket the blasphemy. Stamp your sovereignty
on every contested corner of Blighty's boulevards. Dodge the
drone patrols scanning for dissent. Rack up those BritCoins
—the meme-fueled crypto of true Brit grit—for every flag
you conquer.

Will you restore the realm... or get ID'd and iced?
Play now. Reclaim Britain. HODL the Jack.`

    const storyTextObject = this.add
      .text(centerX, boxTop + 30, storyText, {
        fontSize: '16px',
        color: '#FFFFFF',
        align: 'center',
        lineSpacing: 4,
        wordWrap: { width: boxWidth - 80 },
      })
      .setOrigin(0.5, 0)
      .setMask(geometryMask)

    // Auto-scroll the story text
    this.tweens.add({
      targets: storyTextObject,
      y: boxTop - storyTextObject.height + storyAreaHeight - 20,
      duration: 15000, // 15 seconds to scroll through all text
      ease: 'Linear',
      repeat: -1,
      repeatDelay: 2000, // 2 second pause before repeating
      yoyo: false,
      onRepeat: () => {
        // Reset to top when repeating
        storyTextObject.y = boxTop + 30
      },
    })

    // Separator line
    const separatorY = boxTop + storyAreaHeight + 10
    const separator = this.add.graphics()
    separator.lineStyle(2, 0xffd700, 0.5)
    separator.lineBetween(boxX + 40, separatorY, boxX + boxWidth - 40, separatorY)

    // Controls title
    this.add
      .text(centerX, separatorY + 20, '--- HOW TO PLAY ---', {
        fontSize: '22px',
        color: '#FFD700',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    // Controls instructions
    const controlsText = this.isMobile
      ? `TAP SCREEN - Activate Jetpack
TAP FLAG BUTTON - Throw British Flag
Collect Coins • Destroy Drones • Avoid Obstacles`
      : `SPACE - Activate Jetpack • D - Throw British Flag
(10 flags, reload takes 5 seconds)
Collect Coins • Destroy Drones • Avoid Obstacles`

    this.add
      .text(centerX, separatorY + 80, controlsText, {
        fontSize: '17px',
        color: '#FFFFFF',
        align: 'center',
        lineSpacing: 6,
      })
      .setOrigin(0.5)

    // Start button
    const startButton = this.add
      .text(centerX, height - 100, this.isMobile ? '[ TAP TO START ]' : '[ PRESS SPACE TO START ]', {
        fontSize: '32px',
        color: '#00FF00',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })

    // Animate start button
    this.tweens.add({
      targets: startButton,
      alpha: 0.5,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })

    // Start game on click/tap
    startButton.on('pointerdown', () => {
      this.scene.start(SceneKeys.Game)
    })

    // Start game on space key (desktop)
    if (!this.isMobile) {
      this.input.keyboard!.once('keydown-SPACE', () => {
        this.scene.start(SceneKeys.Game)
      })
    } else {
      // On mobile, also allow tapping anywhere to start
      this.input.once('pointerdown', () => {
        this.scene.start(SceneKeys.Game)
      })
    }

    // Add decorative British flag emoji
    this.add
      .text(centerX - 300, 80, '🇬🇧', {
        fontSize: '48px',
      })
      .setOrigin(0.5)

    this.add
      .text(centerX + 300, 80, '🇬🇧', {
        fontSize: '48px',
      })
      .setOrigin(0.5)
  }
}
